/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import { Typography, CardHeader } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { CRM_SCORE_BANDS, getCrmScoreBand } from "../utils/utils";
import "chart.js/auto";
import { Doughnut } from "react-chartjs-2";

const GaugeContainer = styled("div")(({ theme }) => ({
  height: 200,
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-end",
  paddingBottom: theme.spacing(5),
}));

const GaugeInner = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(10),
  left: "50%",
  transform: "translateX(-50%)",
  textAlign: "center",
  zIndex: 10,
}));

/* The needle and its hub are pure instrument chrome, so they read off the
   text colour rather than a literal - otherwise they vanish on the dark card. */
const Needle = styled("div")(({ theme }) => ({
  position: "absolute",
  left: "50%",
  bottom: theme.spacing(7),
  width: 3,
  height: theme.spacing(18),
  backgroundColor: theme.palette.text.primary,
  transformOrigin: "bottom center",
  zIndex: 5,
  borderRadius: theme.shape.borderRadius,
}));

const NeedleHub = styled("div")(({ theme }) => ({
  position: "absolute",
  left: "50%",
  width: theme.spacing(4),
  height: theme.spacing(4),
  backgroundColor: theme.palette.text.primary,
  borderRadius: "50%",
  transform: "translateX(-50%)",
  zIndex: 6,
  border: `2px solid ${theme.palette.background.paper}`,
}));

const LegendContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(3),
  marginTop: theme.spacing(4),
  justifyContent: "center",
}));

const LegendItem = styled("div", {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  opacity: isActive ? 1 : 0.7,
  fontSize: theme.typography.body1.fontSize,
  fontWeight: isActive
    ? theme.typography.fontWeightBold
    : theme.typography.fontWeightRegular,
}));

const BandSwatch = styled("span", {
  shouldForwardProp: (prop) => prop !== "swatchColor",
})<{ swatchColor: string }>(({ theme, swatchColor }) => ({
  width: 10,
  height: 10,
  backgroundColor: swatchColor,
  borderRadius: theme.shape.borderRadius,
  flexShrink: 0,
  display: "inline-block",
}));

interface DashboardHealthScoreProps {
  scoreStats: any;
}

/**
 * Reads a score off the stats payload, keeping "absent" distinct from "zero".
 *
 * The hook behind this seeds its state with an empty array, so an unscored
 * system and a system scored at 0 both used to arrive here as falsy and both
 * rendered as a red "Critical" needle pinned hard left. A fabricated emergency
 * on a customer's own data costs more than a blank instrument does.
 */
const readScore = (stats: any): number | null => {
  const score = stats?.score;
  return typeof score === "number" && Number.isFinite(score) ? score : null;
};

// The single format for every score shown in this card.
const formatScore = (score: number): string => score.toFixed(1);

const formatBaselineDate = (value: unknown): string | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value as string);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

export const DashboardHealthScore: React.FC<DashboardHealthScoreProps> = ({
  scoreStats,
}) => {
  const theme = useTheme();

  const latestScore = readScore(scoreStats?.latest_stats);
  const startScore = readScore(scoreStats?.start_stats);
  const baselineDate = formatBaselineDate(scoreStats?.start_stats?.date);

  const hasScore = latestScore !== null;
  const currentBand = hasScore ? getCrmScoreBand(latestScore) : null;
  const delta =
    hasScore && startScore !== null ? latestScore - startScore : null;
  const formattedDelta = delta === null ? null : formatScore(Math.abs(delta));
  // The sign follows the formatted value, so a change that rounds to 0.0 has none.
  const deltaSign =
    delta === null || formattedDelta === "0.0"
      ? ""
      : delta > 0
      ? "+"
      : delta < 0
      ? "−"
      : "";

  const gaugeData = {
    datasets: [
      {
        // Unscored draws one flat neutral arc: the instrument is present, it
        // just has nothing to report yet.
        data: hasScore
          ? CRM_SCORE_BANDS.map((band) => band.max - band.min)
          : [100],
        backgroundColor: hasScore
          ? CRM_SCORE_BANDS.map((band) => band.color)
          : [theme.palette.action.disabledBackground],
        borderWidth: 1,
        borderColor: theme.palette.background.paper,
      },
    ],
  };

  const needleRotation = hasScore ? (latestScore / 100) * 180 - 90 : 0;

  const gaugeOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    circumference: 180, // Semicircle
    rotation: 270, // Start from left
    cutout: "75%",
    layout: {
      padding: 0,
    },
  };

  const gaugeLabel = currentBand
    ? `CRM health score ${latestScore} out of 100, band ${currentBand.label}`
    : "CRM health score not yet available for this system";

  return (
    <MiraCard style={{ height: "100%" }}>
      <CardHeader title={"Health Score"} />
      <MiraCardContent>
        <GaugeContainer role="img" aria-label={gaugeLabel}>
          <Doughnut data={gaugeData} options={gaugeOptions} />

          {currentBand && (
            <React.Fragment>
              <Needle
                aria-hidden="true"
                style={{
                  transform: `translateX(-50%) rotate(${needleRotation}deg)`,
                }}
              />
              <NeedleHub aria-hidden="true" />
            </React.Fragment>
          )}

          <GaugeInner>
            {currentBand ? (
              <React.Fragment>
                <Typography variant="h1" component="div" color="textPrimary">
                  {formatScore(latestScore)}
                </Typography>
                {/* The band is named in words and the colour rides alongside as
                    a swatch, so the label itself stays at full text contrast. */}
                <Typography
                  variant="h4"
                  component="div"
                  color="textPrimary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    marginTop: 1,
                  }}
                >
                  <BandSwatch swatchColor={currentBand.color} />
                  {currentBand.label}
                </Typography>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Typography variant="h4" component="div" color="textPrimary">
                  Not yet scored
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ marginTop: 1 }}
                >
                  This system has no completed data quality run.
                </Typography>
              </React.Fragment>
            )}
          </GaugeInner>
        </GaugeContainer>

        <LegendContainer>
          {CRM_SCORE_BANDS.map((band) => (
            <LegendItem key={band.label} isActive={band === currentBand}>
              <BandSwatch swatchColor={band.color} />
              <span>{band.label}</span>
            </LegendItem>
          ))}
        </LegendContainer>

        <Table size={"small"} sx={{ marginTop: 4 }}>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography variant={"body1"} style={{ fontWeight: "bold" }}>
                  Start
                </Typography>
                {baselineDate && (
                  <Typography variant={"body1"} color="textSecondary">
                    {baselineDate}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {startScore === null ? "—" : formatScore(startScore)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant={"body1"} style={{ fontWeight: "bold" }}>
                  Today
                </Typography>
              </TableCell>
              <TableCell>
                {latestScore === null ? "—" : formatScore(latestScore)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography variant={"body1"} style={{ fontWeight: "bold" }}>
                  Change
                </Typography>
              </TableCell>
              {/* Deliberately neutral: the score ramp is the only place in this
                  product where colour is allowed to mean good or bad. */}
              <TableCell>
                {formattedDelta === null
                  ? "—"
                  : `${deltaSign}${formattedDelta}`}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </MiraCardContent>
    </MiraCard>
  );
};
